import React, { PropsWithChildren, useState } from 'react'
import { Box, ListItem, ListItemAvatar, ListItemText, Stack } from '@mui/material'
import { IoChevronDownOutline } from 'react-icons/io5'
import TokenAvatar from '@components/TokenAvatar'
import Touchable from '@components/Touchable'
import { Chain } from '@plug-core/interfaces/common'
import {
  GroupedBalance,
  TokenBalance,
  tokenBalanceToBalanceGroup,
} from '@plug-core/providers/BalanceProvider'
import { formatNumber, formatMoney } from '@plug-core/utils/formatNumber'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import { AnimatePresence, motion } from 'framer-motion'

export interface TokenListItemProps {
  balance: GroupedBalance | TokenBalance
  onClick?: (token: TokenBalance) => void
  showPriceChange: boolean
}

const TokenBalanceItem = ({
  onClick,
  chains,
  logo,
  name,
  balance,
  usdBalance,
  symbol,
  priceChange,
}: {
  onClick?: () => void
  chains: Chain[]
  logo: string
  name: string
  symbol: string
  balance: number
  usdBalance: number | undefined
  priceChange?: number | null
}) => {
  return (
    <Touchable onClick={onClick}>
      <ListItem disablePadding sx={{ background: 'inherit !important' }}>
        <ListItemAvatar>
          <Stack position="relative">
            <TokenAvatar size={44} logo={logo} />
            <Stack direction="row" position="absolute" right={14} bottom={-5}>
              {chains?.map((chain) => (
                <Stack
                  key={chain}
                  bgcolor="background.paper"
                  padding={0.45}
                  borderRadius={4}
                  mr={-1.25}
                >
                  <img src={getChainIcon(chain)} height={15} width={15} />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </ListItemAvatar>
        <ListItemText
          primaryTypographyProps={{
            fontWeight: '600 !important',
          }}
          primary={name}
          secondary={formatNumber(balance, symbol)}
        />
        <ListItemText
          primaryTypographyProps={{
            textAlign: 'right',
          }}
          secondaryTypographyProps={{
            textAlign: 'right',
            color: (priceChange || 0) > 0 ? 'rainbow.green' : 'text.red',
          }}
          primary={formatMoney(usdBalance)}
          secondary={typeof priceChange === 'number' ? `${priceChange?.toFixed(2)}%` : ''}
        />
      </ListItem>
    </Touchable>
  )
}

const ListItemContainer = ({ children, onClick }: PropsWithChildren<{ onClick?: () => void }>) => (
  <Box
    bgcolor="background.paper"
    paddingRight={2.5}
    paddingLeft={2.25}
    paddingY={0.75}
    marginY={0.6}
    borderRadius={1.5}
    onClick={onClick}
    sx={onClick ? { cursor: 'pointer', ':hover': { bgcolor: '#23242b !important' } } : {}}
  >
    {children}
  </Box>
)

export const TokenListItem = ({ balance, onClick, showPriceChange }: TokenListItemProps) => {
  const [expanded, setExpanded] = useState(false)

  const groupBalance = 'balances' in balance ? balance : tokenBalanceToBalanceGroup(balance)
  const { logo, balances, usdBalance, totalBalance, priceChange, name, symbol } = groupBalance
  const isGroup = balances.length > 1

  return (
    <Stack position="relative" justifyContent="center" mx={1.5}>
      <ListItemContainer
        onClick={() => (isGroup ? setExpanded(!expanded) : onClick?.(balances[0]))}
      >
        <TokenBalanceItem
          chains={balances.map((b) => b.token.chain)}
          logo={logo}
          name={name}
          symbol={symbol}
          balance={totalBalance}
          usdBalance={usdBalance}
          priceChange={showPriceChange ? priceChange : null}
        />
      </ListItemContainer>

      {isGroup && expanded && (
        <Box
          mx="auto"
          my={-2.2}
          zIndex={1}
          padding={0.6}
          bgcolor="background.default"
          borderRadius={10}
          display="flex"
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <IoChevronDownOutline size={22} style={{ position: 'relative', top: 1 }} />
        </Box>
      )}

      <AnimatePresence>
        {isGroup && expanded && (
          <motion.section
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            <ListItemContainer>
              {groupBalance.balances.map((tokenBalance) => (
                <Box key={tokenBalance.token.id} my={1}>
                  <TokenBalanceItem
                    onClick={() => onClick?.(tokenBalance)}
                    chains={[tokenBalance.token.chain]}
                    logo={tokenBalance.token.logo}
                    name={tokenBalance.token.name}
                    symbol={tokenBalance.token.symbol}
                    balance={tokenBalance.balance}
                    usdBalance={tokenBalance.usdBalance}
                  />
                </Box>
              ))}
            </ListItemContainer>
          </motion.section>
        )}
      </AnimatePresence>
    </Stack>
  )
}
